import jsPDF from 'jspdf';
import { MedicalCondition, PDPMRankingAnalysis } from '../types/codeIdentifier';

export interface PDFReportData {
  finalEvaluation: MedicalCondition[];
  pdpmRankingAnalysis: PDPMRankingAnalysis | null;
  patientNames: string;
  generatedBy?: string;
  facilityName?: string;
}

export class PDFReportService {
  private pdf: jsPDF;
  private pageWidth: number;
  private pageHeight: number;
  private margin: number = 25;
  private currentY: number = 25;
  private readonly primaryColor = '#1890ff';
  private readonly secondaryColor = '#52c41a';
  private readonly warningColor = '#faad14';
  private readonly dangerColor = '#ff4d4f';
  private readonly lightGray = '#f5f5f5';

  // Data cleaning helpers
  private cleanTextValue(value: any): string {
    if (typeof value === 'string') {
      return value;
    }
    if (typeof value === 'object' && value !== null) {
      // Try to extract meaningful text from object
      if (value.toString && value.toString() !== '[object Object]') {
        return value.toString();
      }
      // Return a fallback message for objects
      return 'Data available in system';
    }
    return String(value || '');
  }

  private cleanReasoning(reasoning: any): string {
    const cleaned = this.cleanTextValue(reasoning);
    
    // If the reasoning is already clean (contains bullet points), return as is
    if (cleaned.includes('•') || cleaned.includes('NTA point')) {
      return cleaned;
    }
    
    // Remove technical prefixes and extract meaningful content
    const patterns = [
      /Database lookup:\s*/i,
      /vector_search_score=[\d.]+\s*/g,
      /short_description=.*?(?=\s|$)/g,
      /reasoning=.*?(?=\s|$)/g
    ];
    
    let result = cleaned;
    patterns.forEach(pattern => {
      result = result.replace(pattern, '');
    });
    
    return result.trim() || 'Database lookup completed';
  }

  private cleanImpact(impact: any): string {
    const cleaned = this.cleanTextValue(impact);
    
    // Similar cleaning for impact fields
    const patterns = [
      /Database lookup:\s*/i,
      /vector_search_score=[\d.]+\s*/g,
      /impact=.*?(?=\s|$)/g
    ];
    
    let result = cleaned;
    patterns.forEach(pattern => {
      result = result.replace(pattern, '');
    });
    
    return result.trim() || 'Impact assessment available';
  }

  constructor() {
    this.pdf = new jsPDF('p', 'mm', 'a4');
    this.pageWidth = this.pdf.internal.pageSize.getWidth();
    this.pageHeight = this.pdf.internal.pageSize.getHeight();
  }

  public generateCategoricalReport(data: PDFReportData): void {
    this.resetPDF();
    this.addHeader(data.patientNames, data.facilityName, 'Categorical Analysis Report');
    this.addCategoricalSummarySection(data);
    
    if (data.finalEvaluation.length > 0) {
      this.addCategoricalAnalysisSection(data.finalEvaluation);
    }
    
    this.addFooter(data.generatedBy);
    this.addPageNumbers();
    
    const fileName = this.generateFileName(data.patientNames, 'Categorical');
    this.pdf.save(fileName);
  }

  public generatePDPMReport(data: PDFReportData): void {
    this.resetPDF();
    this.addHeader(data.patientNames, data.facilityName, 'PDPM Ranking Analysis Report');
    this.addPDPMSummarySection(data);
    
    if (data.pdpmRankingAnalysis) {
      this.addPDPMRankingSection(data.pdpmRankingAnalysis);
    }
    
    this.addFooter(data.generatedBy);
    this.addPageNumbers();
    
    const fileName = this.generateFileName(data.patientNames, 'PDPM');
    this.pdf.save(fileName);
  }

  private resetPDF(): void {
    this.pdf = new jsPDF('p', 'mm', 'a4');
    this.pageWidth = this.pdf.internal.pageSize.getWidth();
    this.pageHeight = this.pdf.internal.pageSize.getHeight();
    this.currentY = 25;
  }

  private addHeader(patientNames: string, facilityName?: string, reportTitle?: string): void {
    // Header background - light blue
    this.pdf.setFillColor(240, 248, 255);
    this.pdf.rect(0, 0, this.pageWidth, 70, 'F');
    
    // Company logo area
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.setFontSize(28);
    this.pdf.setTextColor(24, 144, 255);
    this.pdf.text('CodePilot', this.margin, 25);
    
    // Report title
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.setFontSize(18);
    this.pdf.setTextColor(31, 31, 31);
    this.pdf.text(reportTitle || 'ICD-10 Code Analysis Report', this.margin, 45);
    
    // Patient info
    this.pdf.setFont('helvetica', 'normal');
    this.pdf.setFontSize(12);
    this.pdf.setTextColor(102, 102, 102);
    
    if (patientNames) {
      this.pdf.text(`Patient(s): ${patientNames}`, this.margin, 58);
    }
    
    if (facilityName) {
      this.pdf.text(`Facility: ${facilityName}`, this.margin, 65);
    }
    
    // Generation date (right aligned)
    const dateText = `Generated: ${new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })}`;
    this.pdf.setFontSize(10);
    const dateWidth = this.pdf.getTextWidth(dateText);
    this.pdf.text(dateText, this.pageWidth - this.margin - dateWidth, 58);
    
    // Add separator line
    this.pdf.setDrawColor(200, 200, 200);
    this.pdf.setLineWidth(0.5);
    this.pdf.line(this.margin, 72, this.pageWidth - this.margin, 72);
    
    this.currentY = 85;
  }

  private addCategoricalSummarySection(data: PDFReportData): void {
    this.addSectionTitle('Executive Summary', this.primaryColor);
    
    // Helper function to get all codes from a condition
    const getCodesFromCondition = (condition: any) => {
      if (condition.best_icd10_codes && condition.best_icd10_codes.length > 0) {
        return condition.best_icd10_codes;
      }
      // Fallback for backward compatibility
      if (condition.best_icd10_code) {
        return [condition.best_icd10_code];
      }
      return [];
    };

    const validCodesCount = data.finalEvaluation.filter(condition => {
      const codes = getCodesFromCondition(condition);
      return codes.some((code: any) => 
        code.code && !['NO_CODE', 'UNKNOWN'].includes(code.code)
      );
    }).length;
    
    const totalConditions = data.finalEvaluation.length;
    const categoriesCount = new Set(
      data.finalEvaluation.map(condition => condition.category || 'Uncategorized')
    ).size;
    
    // Summary stats in a well-formatted bordered box
    this.pdf.setDrawColor(220, 220, 220);
    this.pdf.setFillColor(248, 250, 252);
    this.pdf.setLineWidth(0.5);
    const boxHeight = 35;
    this.pdf.rect(this.margin, this.currentY, this.pageWidth - 2 * this.margin, boxHeight, 'FD');
    
    this.pdf.setFont('helvetica', 'normal');
    this.pdf.setFontSize(11);
    this.pdf.setTextColor(31, 31, 31);
    
    const summaryY = this.currentY + 12;
    this.pdf.text(`Total Medical Conditions Identified: ${totalConditions}`, this.margin + 15, summaryY);
    this.pdf.text(`Valid ICD-10 Codes Found: ${validCodesCount}`, this.margin + 15, summaryY + 8);
    this.pdf.text(`Medical Categories: ${categoriesCount}`, this.margin + 15, summaryY + 16);
    
    this.currentY += boxHeight + 15;
    this.checkPageBreak(20);
  }

  private addPDPMSummarySection(data: PDFReportData): void {
    this.addSectionTitle('Executive Summary', this.primaryColor);
    
    if (!data.pdpmRankingAnalysis) {
      this.pdf.setFont('helvetica', 'italic');
      this.pdf.setFontSize(11);
      this.pdf.setTextColor(102, 102, 102);
      this.pdf.text('No PDPM ranking analysis available for this patient.', this.margin, this.currentY);
      this.currentY += 25;
      return;
    }
    
    // Summary stats in a well-formatted bordered box
    this.pdf.setDrawColor(220, 220, 220);
    this.pdf.setFillColor(248, 250, 252);
    this.pdf.setLineWidth(0.5);
    const boxHeight = 55;
    this.pdf.rect(this.margin, this.currentY, this.pageWidth - 2 * this.margin, boxHeight, 'FD');
    
    this.pdf.setFont('helvetica', 'normal');
    this.pdf.setFontSize(11);
    this.pdf.setTextColor(31, 31, 31);
    
    const summaryY = this.currentY + 15;
    const lineHeight = 9;
    
    this.pdf.text(`Primary Diagnosis Recommendation: ${
      typeof data.pdpmRankingAnalysis.primary_diagnosis_recommendation?.code === 'string' 
        ? data.pdpmRankingAnalysis.primary_diagnosis_recommendation.code 
        : data.pdpmRankingAnalysis.primary_diagnosis_recommendation?.code?.code || 'None'
    }`, this.margin + 15, summaryY);
    
    this.pdf.text(`Alternative Options: ${
      data.pdpmRankingAnalysis.primary_diagnosis_alternatives.length
    }`, this.margin + 15, summaryY + lineHeight);
    
    this.pdf.text(`Secondary Diagnoses: ${
      data.pdpmRankingAnalysis.non_primary_diagnoses.length
    }`, this.margin + 15, summaryY + lineHeight * 2);
    
    this.pdf.text(`NTA Qualifying Codes: ${
      data.pdpmRankingAnalysis.nta_codes.length
    }`, this.margin + 15, summaryY + lineHeight * 3);
    
    this.pdf.text(`SLP Related Codes: ${
      data.pdpmRankingAnalysis.slp_codes.length
    }`, this.margin + 15, summaryY + lineHeight * 4);
    
    this.currentY += boxHeight + 10;
    this.checkPageBreak(30);
  }

  private addSummarySection(data: PDFReportData): void {
    this.addSectionTitle('Executive Summary', this.primaryColor);
    
    // Helper function to get all codes from a condition
    const getCodesFromCondition = (condition: any) => {
      if (condition.best_icd10_codes && condition.best_icd10_codes.length > 0) {
        return condition.best_icd10_codes;
      }
      // Fallback for backward compatibility
      if (condition.best_icd10_code) {
        return [condition.best_icd10_code];
      }
      return [];
    };

    const validCodesCount = data.finalEvaluation.filter(condition => {
      const codes = getCodesFromCondition(condition);
      return codes.some((code: any) => 
        code.code && !['NO_CODE', 'UNKNOWN'].includes(code.code)
      );
    }).length;
    
    const totalConditions = data.finalEvaluation.length;
    const categoriesCount = new Set(
      data.finalEvaluation.map(condition => condition.category || 'Uncategorized')
    ).size;
    
    // Summary stats in a well-formatted bordered box
    this.pdf.setDrawColor(220, 220, 220);
    this.pdf.setFillColor(248, 250, 252);
    this.pdf.setLineWidth(0.5);
    const boxHeight = data.pdpmRankingAnalysis ? 55 : 35;
    this.pdf.rect(this.margin, this.currentY, this.pageWidth - 2 * this.margin, boxHeight, 'FD');
    
    this.pdf.setFont('helvetica', 'normal');
    this.pdf.setFontSize(11);
    this.pdf.setTextColor(31, 31, 31);
    
    const summaryY = this.currentY + 12;
    this.pdf.text(`Total Medical Conditions Identified: ${totalConditions}`, this.margin + 15, summaryY);
    this.pdf.text(`Valid ICD-10 Codes Found: ${validCodesCount}`, this.margin + 15, summaryY + 8);
    this.pdf.text(`Medical Categories: ${categoriesCount}`, this.margin + 15, summaryY + 16);
    
    if (data.pdpmRankingAnalysis) {
      this.pdf.text(`Primary Diagnosis Recommendation: ${
        typeof data.pdpmRankingAnalysis.primary_diagnosis_recommendation?.code === 'string' 
          ? data.pdpmRankingAnalysis.primary_diagnosis_recommendation.code 
          : data.pdpmRankingAnalysis.primary_diagnosis_recommendation?.code?.code || 'None'
      }`, this.margin + 15, summaryY + 24);
      
      this.pdf.text(`Alternative Options: ${
        data.pdpmRankingAnalysis.primary_diagnosis_alternatives.length
      }`, this.margin + 15, summaryY + 32);
      
      this.pdf.text(`NTA Qualifying Codes: ${
        data.pdpmRankingAnalysis.nta_codes.length
      }`, this.margin + 15, summaryY + 40);
    }
    
    this.currentY += boxHeight + 15;
    this.checkPageBreak(20);
  }

  private addCategoricalAnalysisSection(conditions: MedicalCondition[]): void {
    this.addSectionTitle('Categorical Analysis', this.secondaryColor);
    
    // Group conditions by category
    const grouped = conditions.reduce((acc, condition) => {
      const category = condition.category || 'Uncategorized';
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(condition);
      return acc;
    }, {} as Record<string, MedicalCondition[]>);

    Object.entries(grouped)
      .sort(([a], [b]) => a.localeCompare(b))
      .forEach(([category, categoryConditions]) => {
        this.addCategorySection(category, categoryConditions);
      });
  }

  private addCategorySection(category: string, conditions: MedicalCondition[]): void {
    this.checkPageBreak(40);
    
    // Category header with background
    this.pdf.setFillColor(240, 240, 240);
    this.pdf.setDrawColor(200, 200, 200);
    this.pdf.rect(this.margin - 5, this.currentY - 5, this.pageWidth - 2 * this.margin + 10, 18, 'FD');
    
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.setFontSize(13);
    this.pdf.setTextColor(31, 31, 31);
    this.pdf.text(category, this.margin, this.currentY + 8);
    
    this.currentY += 25;
    
    conditions.forEach((condition, index) => {
      this.addConditionEntry(condition, index === conditions.length - 1);
    });
    
    this.currentY += 15;
  }

  private addConditionEntry(condition: MedicalCondition, isLast: boolean): void {
    this.checkPageBreak(35);
    
    // Condition name (bold, larger)
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.setFontSize(11);
    this.pdf.setTextColor(31, 31, 31);
    
    const conditionLines = this.pdf.splitTextToSize(
      condition.condition_name, 
      this.pageWidth - 2 * this.margin - 20
    );
    this.pdf.text(conditionLines, this.margin + 10, this.currentY);
    this.currentY += conditionLines.length * 5 + 5;
    
    // Helper function to get all codes from a condition
    const getCodesFromCondition = (condition: any) => {
      if (condition.best_icd10_codes && condition.best_icd10_codes.length > 0) {
        return condition.best_icd10_codes;
      }
      // Fallback for backward compatibility
      if (condition.best_icd10_code) {
        return [condition.best_icd10_code];
      }
      return [];
    };

    // ICD-10 Codes (handle multiple codes)
    const codes = getCodesFromCondition(condition);
    const validCodes = codes.filter((code: any) => 
      code.code && !['NO_CODE', 'UNKNOWN'].includes(code.code)
    );

    if (validCodes.length > 0) {
      validCodes.forEach((code: any, codeIndex: number) => {
        // Add a small gap between multiple codes
        if (codeIndex > 0) {
          this.currentY += 5;
        }

        // Code in a colored box
        this.pdf.setFillColor(230, 244, 255);
        this.pdf.setDrawColor(24, 144, 255);
        this.pdf.rect(this.margin + 10, this.currentY - 3, 40, 12, 'FD');
        
        this.pdf.setFont('helvetica', 'bold');
        this.pdf.setFontSize(10);
        this.pdf.setTextColor(24, 144, 255);
        this.pdf.text(`${code.code}`, this.margin + 15, this.currentY + 5);
        
        // Description
        this.pdf.setFont('helvetica', 'normal');
        this.pdf.setFontSize(10);
        this.pdf.setTextColor(31, 31, 31);
        const descLines = this.pdf.splitTextToSize(
          code.short_description,
          this.pageWidth - 2 * this.margin - 65
        );
        this.pdf.text(descLines, this.margin + 60, this.currentY + 5);
        this.currentY += Math.max(12, descLines.length * 5) + 5;
        
        // Reasoning (if available)
        if (code.reasoning) {
          this.pdf.setFont('helvetica', 'italic');
          this.pdf.setFontSize(9);
          this.pdf.setTextColor(102, 102, 102);
          const reasoningLines = this.pdf.splitTextToSize(
            `Reasoning: ${code.reasoning}`,
            this.pageWidth - 2 * this.margin - 20
          );
          this.pdf.text(reasoningLines, this.margin + 15, this.currentY);
          this.currentY += reasoningLines.length * 4 + 5;
        }
      });
    } else {
      this.pdf.setFont('helvetica', 'italic');
      this.pdf.setFontSize(10);
      this.pdf.setTextColor(255, 77, 79);
      this.pdf.text('No suitable ICD-10 code found', this.margin + 15, this.currentY);
      this.currentY += 10;
    }
    
    // Add separator line if not last
    if (!isLast) {
      this.pdf.setDrawColor(240, 240, 240);
      this.pdf.setLineWidth(0.5);
      this.pdf.line(this.margin + 10, this.currentY + 3, this.pageWidth - this.margin - 10, this.currentY + 3);
      this.currentY += 10;
    } else {
      this.currentY += 8;
    }
  }

  private addPDPMRankingSection(analysis: PDPMRankingAnalysis): void {
    // Check if we have enough space for the section title AND primary diagnosis content
    // Section title: ~20mm, primary diagnosis subtitle: ~20mm, box: ~55-80mm = 120mm total
    this.checkPageBreak(120);
    this.addSectionTitle('PDPM Ranking Analysis', this.warningColor);
    
    // Primary Diagnosis Recommendation
    if (analysis.primary_diagnosis_recommendation) {
      this.addPDPMPrimaryDiagnosis(analysis.primary_diagnosis_recommendation);
    }
    
    // Primary Diagnosis Alternatives
    if (analysis.primary_diagnosis_alternatives.length > 0) {
      this.addPDPMAlternatives(analysis.primary_diagnosis_alternatives);
    }
    
    // Secondary Diagnoses
    if (analysis.non_primary_diagnoses.length > 0) {
      this.addPDPMNonPrimary(analysis.non_primary_diagnoses);
    }
    
    // NTA Codes
    if (analysis.nta_codes.length > 0) {
      this.addPDPMNTACodes(analysis.nta_codes);
    }
    
    // SLP Codes
    if (analysis.slp_codes.length > 0) {
      this.addPDPMSLPCodes(analysis.slp_codes);
    }
  }

  private addPDPMPrimaryDiagnosis(recommendation: {
    code: any;
    justification: string;
  }): void {
    // Space is already checked in parent method, just proceed
    
    // Subsection title
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.setFontSize(14);
    this.pdf.setTextColor(250, 173, 20);
    this.pdf.text('Primary Diagnosis Recommendation', this.margin, this.currentY);
    this.currentY += 20;
    
    // Calculate text heights properly
    const justificationText = `Justification: ${recommendation.justification}`;
    const justificationLines = this.pdf.splitTextToSize(
      justificationText,
      this.pageWidth - 2 * this.margin - 30
    );
    const justificationHeight = justificationLines.length * 5;
    
    // Store starting position for the box
    const boxStartY = this.currentY;
    
    // Calculate proper box height: header space (45mm) + justification + padding (10mm)
    const boxHeight = 45 + justificationHeight + 10;
    
    // Highlighted box for primary recommendation with proper height calculation
    this.pdf.setDrawColor(250, 173, 20);
    this.pdf.setFillColor(255, 248, 230);
    this.pdf.setLineWidth(1);
    this.pdf.rect(this.margin, boxStartY, this.pageWidth - 2 * this.margin, boxHeight, 'FD');
    
    // Code (large and prominent)
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.setFontSize(18);
    this.pdf.setTextColor(24, 144, 255);
    const codeString = typeof recommendation.code === 'string' ? recommendation.code : recommendation.code.code;
    this.pdf.text(codeString, this.margin + 15, boxStartY + 18);
    
    // Clinical category
    this.pdf.setFont('helvetica', 'normal');
    this.pdf.setFontSize(12);
    this.pdf.setTextColor(31, 31, 31);
    const clinicalCategory = typeof recommendation.code === 'string' ? 'N/A' : recommendation.code.clinical_category || 'N/A';
    this.pdf.text(`Clinical Category: ${clinicalCategory}`, this.margin + 15, boxStartY + 32);
    
    // Justification with better formatting
    this.pdf.setFont('helvetica', 'normal');
    this.pdf.setFontSize(10);
    this.pdf.setTextColor(102, 102, 102);
    this.pdf.text(justificationLines, this.margin + 15, boxStartY + 45);
    
    // Move currentY to end of box plus small margin
    this.currentY = boxStartY + boxHeight + 10;
    this.checkPageBreak(30);
  }

  private addPDPMAlternatives(alternatives: Array<{
    code: any;
    justification: string;
    rank: number;
  }>): void {
    this.checkPageBreak(40);
    
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.setFontSize(13);
    this.pdf.setTextColor(24, 144, 255);
    this.pdf.text(`Primary Diagnosis Alternatives (${alternatives.length})`, this.margin, this.currentY);
    this.currentY += 18;
    
    alternatives
      .sort((a, b) => (a.rank || 0) - (b.rank || 0))
      .forEach((alt, index) => {
        this.checkPageBreak(35);
        
        // Calculate proper box height
        const justificationLines = this.pdf.splitTextToSize(
          alt.justification,
          this.pageWidth - 2 * this.margin - 30
        );
        const boxHeight = 35 + (justificationLines.length * 4);
        
        // Alternative item box
        this.pdf.setDrawColor(230, 230, 230);
        this.pdf.setFillColor(248, 250, 252);
        this.pdf.rect(this.margin + 5, this.currentY, this.pageWidth - 2 * this.margin - 10, boxHeight, 'FD');
        
        // Code and rank
        this.pdf.setFont('helvetica', 'bold');
        this.pdf.setFontSize(12);
        this.pdf.setTextColor(31, 31, 31);
        const codeString = typeof alt.code === 'string' ? alt.code : alt.code.code;
        this.pdf.text(`${index + 1}. ${codeString}`, this.margin + 10, this.currentY + 12);
        
        // Clinical category
        this.pdf.setFont('helvetica', 'normal');
        this.pdf.setFontSize(10);
        this.pdf.setTextColor(102, 102, 102);
        const clinicalCategory = typeof alt.code === 'string' ? 'N/A' : alt.code.clinical_category || 'N/A';
        this.pdf.text(`Clinical Category: ${clinicalCategory}`, this.margin + 10, this.currentY + 22);
        
        // Justification with proper spacing
        this.pdf.setFont('helvetica', 'normal');
        this.pdf.setFontSize(9);
        this.pdf.setTextColor(102, 102, 102);
        this.pdf.text(justificationLines, this.margin + 10, this.currentY + 32);
        
        this.currentY += boxHeight + 10;
      });
    
    this.currentY += 10;
  }

  private addPDPMNonPrimary(nonPrimary: Array<{
    code: any;
    description: string;
    complexity_impact: string;
    reimbursement_impact: string;
    should_document: boolean;
  }>): void {
    this.checkPageBreak(30);
    
    // Helper function to extract code string
    const getCodeString = (item: any) => typeof item.code === 'string' ? item.code : item.code.code;
    
    // Filter, deduplicate, and sort by impact
    const highImpactCodes = nonPrimary
      .filter(item => item.should_document)
      .filter((item, index, self) => {
        const codeString = getCodeString(item);
        return self.findIndex(x => getCodeString(x) === codeString) === index;
      })
      .sort((a, b) => {
        const getScore = (item: typeof a) => {
          let score = 0;
          if (item.complexity_impact.toLowerCase().includes('significant')) score += 3;
          if (item.reimbursement_impact.toLowerCase().includes('significant')) score += 3;
          if (item.complexity_impact.toLowerCase().includes('moderate')) score += 2;
          if (item.reimbursement_impact.toLowerCase().includes('moderate')) score += 2;
          return score;
        };
        return getScore(b) - getScore(a);
      });
    
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.setFontSize(13);
    this.pdf.setTextColor(82, 196, 26);
    this.pdf.text(`Secondary Diagnoses (${highImpactCodes.length})`, this.margin, this.currentY);
    this.currentY += 18;
    
    highImpactCodes.forEach((item, index) => {
      // Calculate text heights for proper box sizing
      const maxWidth = this.pageWidth - 2 * this.margin - 20;
      
      // Split text to fit within the box width
      const cleanedComplexity = this.cleanImpact(item.complexity_impact);
      const cleanedReimbursement = this.cleanImpact(item.reimbursement_impact);
      const complexityLines = this.pdf.splitTextToSize(
        `Complexity: ${cleanedComplexity}`,
        maxWidth
      );
      const reimbursementLines = this.pdf.splitTextToSize(
        `Reimbursement: ${cleanedReimbursement}`,
        maxWidth
      );
      
      // Calculate required height based on content
      const lineHeight = 4;
      const paddingTop = 8;
      const paddingBottom = 4;
      const codeLine = 1;
      const totalLines = codeLine + complexityLines.length + reimbursementLines.length;
      const boxHeight = paddingTop + (totalLines * lineHeight) + paddingBottom;
      
      this.checkPageBreak(boxHeight + 5);
      
      // Add background box with dynamic height
      this.pdf.setDrawColor(240, 240, 240);
      this.pdf.setFillColor(250, 250, 250);
      this.pdf.rect(this.margin, this.currentY, this.pageWidth - 2 * this.margin, boxHeight, 'FD');
      
      let textY = this.currentY + paddingTop;
      
      // Code (bold)
      this.pdf.setFont('helvetica', 'bold');
      this.pdf.setFontSize(11);
      this.pdf.setTextColor(31, 31, 31);
      const codeString = typeof item.code === 'string' ? item.code : item.code.code;
      this.pdf.text(`${codeString}`, this.margin + 10, textY);
      textY += lineHeight + 2;
      
      // Complexity (with proper wrapping)
      this.pdf.setFont('helvetica', 'normal');
      this.pdf.setFontSize(9);
      this.pdf.setTextColor(102, 102, 102);
      complexityLines.forEach((line: string) => {
        this.pdf.text(line, this.margin + 10, textY);
        textY += lineHeight;
      });
      
      // Small gap between complexity and reimbursement
      textY += 1;
      
      // Reimbursement (with proper wrapping)
      reimbursementLines.forEach((line: string) => {
        this.pdf.text(line, this.margin + 10, textY);
        textY += lineHeight;
      });
      
      this.currentY += boxHeight + 5;
    });
    
    this.currentY += 10;
  }

  private addPDPMNTACodes(ntaCodes: Array<{
    code: any;
    nta_points: number;
    reasoning: string;
  }>): void {
    this.checkPageBreak(30);
    
    // Helper function to extract code string
    const getCodeString = (item: any) => typeof item.code === 'string' ? item.code : item.code.code;
    
    // Deduplicate and sort by NTA points (highest first)
    const uniqueNtaCodes = ntaCodes
      .filter((item, index, self) => {
        const codeString = getCodeString(item);
        return self.findIndex(x => getCodeString(x) === codeString) === index;
      })
      .sort((a, b) => b.nta_points - a.nta_points);
    
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.setFontSize(13);
    this.pdf.setTextColor(147, 20, 255);
    this.pdf.text(`NTA Qualifying Codes (${uniqueNtaCodes.length})`, this.margin, this.currentY);
    this.currentY += 18;
    
    uniqueNtaCodes.forEach((item) => {
        this.checkPageBreak(20);
        
        // Fixed box height without reasoning
        const boxHeight = 30;
        
        // Add background box with better styling
        this.pdf.setDrawColor(200, 200, 200);
        this.pdf.setFillColor(250, 252, 255);
        this.pdf.roundedRect(this.margin, this.currentY, this.pageWidth - 2 * this.margin, boxHeight, 3, 3, 'FD');
        
        // Code and points header
        this.pdf.setFont('helvetica', 'bold');
        this.pdf.setFontSize(12);
        this.pdf.setTextColor(31, 31, 31);
        const codeString = typeof item.code === 'string' ? item.code : item.code.code;
        this.pdf.text(`${codeString}`, this.margin + 12, this.currentY + 12);
        
        this.pdf.setFont('helvetica', 'bold');
        this.pdf.setFontSize(10);
        this.pdf.setTextColor(147, 20, 255);
        this.pdf.text(`${item.nta_points.toLocaleString()} NTA Points`, this.margin + 12, this.currentY + 22);
        
        this.currentY += boxHeight + 12;
      });
    
    this.currentY += 10;
  }

  private addPDPMSLPCodes(slpCodes: Array<{
    code: any;
    slp_relevance: string;
    impact: string;
  }>): void {
    this.checkPageBreak(30);
    
    // Helper function to extract code string
    const getCodeString = (item: any) => typeof item.code === 'string' ? item.code : item.code.code;
    
    // Deduplicate SLP codes
    const uniqueSlpCodes = slpCodes.filter((item, index, self) => {
      const codeString = getCodeString(item);
      return self.findIndex(x => getCodeString(x) === codeString) === index;
    });
    
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.setFontSize(13);
    this.pdf.setTextColor(235, 47, 150);
    this.pdf.text(`SLP Related Codes (${uniqueSlpCodes.length})`, this.margin, this.currentY);
    this.currentY += 18;
    
    uniqueSlpCodes.forEach((item) => {
      // Calculate text heights for proper box sizing
      const maxWidth = this.pageWidth - 2 * this.margin - 20;
      
      // Split text to fit within the box width
      const cleanedRelevance = this.cleanImpact(item.slp_relevance);
      const cleanedImpact = this.cleanImpact(item.impact);
      const relevanceLines = this.pdf.splitTextToSize(
        `Relevance: ${cleanedRelevance}`,
        maxWidth
      );
      const impactLines = this.pdf.splitTextToSize(
        `Impact: ${cleanedImpact}`,
        maxWidth
      );
      
      // Calculate required height based on content
      const lineHeight = 4;
      const paddingTop = 8;
      const paddingBottom = 4;
      const codeLine = 1;
      const totalLines = codeLine + relevanceLines.length + impactLines.length;
      const boxHeight = paddingTop + (totalLines * lineHeight) + paddingBottom;
      
      this.checkPageBreak(boxHeight + 5);
      
      // Add background box with dynamic height
      this.pdf.setDrawColor(240, 200, 220);
      this.pdf.setFillColor(252, 248, 250);
      this.pdf.rect(this.margin, this.currentY, this.pageWidth - 2 * this.margin, boxHeight, 'FD');
      
      let textY = this.currentY + paddingTop;
      
      // Code (bold)
      this.pdf.setFont('helvetica', 'bold');
      this.pdf.setFontSize(11);
      this.pdf.setTextColor(31, 31, 31);
              const codeString = typeof item.code === 'string' ? item.code : item.code.code;
        this.pdf.text(`${codeString}`, this.margin + 10, textY);
      textY += lineHeight + 2;
      
      // Relevance (with proper wrapping)
      this.pdf.setFont('helvetica', 'normal');
      this.pdf.setFontSize(9);
      this.pdf.setTextColor(102, 102, 102);
      relevanceLines.forEach((line: string) => {
        this.pdf.text(line, this.margin + 10, textY);
        textY += lineHeight;
      });
      
      // Small gap between relevance and impact
      textY += 1;
      
      // Impact (with proper wrapping)
      impactLines.forEach((line: string) => {
        this.pdf.text(line, this.margin + 10, textY);
        textY += lineHeight;
      });
      
      this.currentY += boxHeight + 5;
    });
    
    this.currentY += 10;
  }

  private addSectionTitle(title: string, color: string): void {
    this.checkPageBreak(20);
    
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.setFontSize(16);
    
    // Convert hex color to RGB
    const r = parseInt(color.slice(1, 3), 16);
    const g = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);
    
    this.pdf.setTextColor(r, g, b);
    this.pdf.text(title, this.margin, this.currentY);
    
    // Add underline with better styling
    const textWidth = this.pdf.getTextWidth(title);
    this.pdf.setDrawColor(r, g, b);
    this.pdf.setLineWidth(1);
    this.pdf.line(this.margin, this.currentY + 3, this.margin + textWidth, this.currentY + 3);
    
    this.currentY += 20;
  }



  private checkPageBreak(requiredSpace: number): void {
    if (this.currentY + requiredSpace > this.pageHeight - 60) {
      this.pdf.addPage();
      this.currentY = this.margin;
    }
  }

  private addFooter(generatedBy?: string): void {
    const totalPages = this.pdf.getNumberOfPages();
    
    for (let i = 1; i <= totalPages; i++) {
      this.pdf.setPage(i);
      
      // Footer background
      this.pdf.setFillColor(248, 250, 252);
      this.pdf.rect(0, this.pageHeight - 35, this.pageWidth, 35, 'F');
      
      // Footer line
      this.pdf.setDrawColor(220, 220, 220);
      this.pdf.line(this.margin, this.pageHeight - 35, this.pageWidth - this.margin, this.pageHeight - 35);
      
      // Footer text with better formatting
      this.pdf.setFont('helvetica', 'normal');
      this.pdf.setFontSize(8);
      this.pdf.setTextColor(75, 85, 99);
      
      const disclaimerText = 'This report is for informational purposes only and should not replace professional medical judgment.';
      this.pdf.text(disclaimerText, this.margin, this.pageHeight - 20);
      
      if (generatedBy) {
        const generatedText = `Generated by: ${generatedBy}`;
        const generatedWidth = this.pdf.getTextWidth(generatedText);
        this.pdf.text(generatedText, this.pageWidth - this.margin - generatedWidth, this.pageHeight - 20);
      }
    }
  }

  private addPageNumbers(): void {
    const totalPages = this.pdf.getNumberOfPages();
    
    for (let i = 1; i <= totalPages; i++) {
      this.pdf.setPage(i);
      this.pdf.setFont('helvetica', 'normal');
      this.pdf.setFontSize(8);
      this.pdf.setTextColor(75, 85, 99);
      
      const pageText = `Page ${i} of ${totalPages}`;
      const pageWidth = this.pdf.getTextWidth(pageText);
      this.pdf.text(pageText, (this.pageWidth - pageWidth) / 2, this.pageHeight - 12);
    }
  }

  private generateFileName(patientNames: string, reportType?: string): string {
    const timestamp = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
    const safeName = patientNames 
      ? patientNames.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '_').slice(0, 30)
      : 'Patient';
    const typePrefix = reportType ? `_${reportType}` : '';
    return `CodePilot_ICD10${typePrefix}_Report_${safeName}_${timestamp}.pdf`;
  }
}

export const pdfReportService = new PDFReportService(); 